import { createContext, useContext, useMemo, useReducer, type ReactNode } from 'react';

export type ActiveTab = 'all' | 'project';

export interface FilterState {
  tags: string[];
  from: string;
  to: string;
  unassigned: boolean;
  q: string;
  sort: 'created_at' | 'title';
  order: 'asc' | 'desc';
}

export const emptyFilters: FilterState = {
  tags: [],
  from: '',
  to: '',
  unassigned: false,
  q: '',
  sort: 'created_at',
  order: 'desc',
};

interface AppState {
  selectedProjectId: string | null;
  activeTab: ActiveTab;
  filters: FilterState;
  refreshKey: number;
}

type Action =
  | { type: 'SELECT_PROJECT'; projectId: string | null }
  | { type: 'SET_TAB'; tab: ActiveTab }
  | { type: 'SET_FILTERS'; filters: Partial<FilterState> }
  | { type: 'CLEAR_FILTERS' }
  | { type: 'REFRESH' };

function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'SELECT_PROJECT':
      // Selecting a project also switches the tab to "Project Bookmarks" so
      // the user immediately sees the contents of what they just clicked.
      // Deselecting (projectId === null) leaves the current tab alone — it
      // would be jarring to flip away from "Project Bookmarks" mid-task.
      return {
        ...state,
        selectedProjectId: action.projectId,
        activeTab: action.projectId !== null ? 'project' : state.activeTab,
      };
    case 'SET_TAB':
      return { ...state, activeTab: action.tab };
    case 'SET_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.filters } };
    case 'CLEAR_FILTERS':
      return { ...state, filters: emptyFilters };
    case 'REFRESH':
      return { ...state, refreshKey: state.refreshKey + 1 };
    default:
      return state;
  }
}

const initialState: AppState = {
  selectedProjectId: null,
  activeTab: 'all',
  filters: emptyFilters,
  refreshKey: 0,
};

interface AppContextValue {
  state: AppState;
  selectProject(id: string | null): void;
  setTab(tab: ActiveTab): void;
  setFilters(filters: Partial<FilterState>): void;
  clearFilters(): void;
  refresh(): void;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const value = useMemo<AppContextValue>(
    () => ({
      state,
      selectProject: (projectId) => dispatch({ type: 'SELECT_PROJECT', projectId }),
      setTab: (tab) => dispatch({ type: 'SET_TAB', tab }),
      setFilters: (filters) => dispatch({ type: 'SET_FILTERS', filters }),
      clearFilters: () => dispatch({ type: 'CLEAR_FILTERS' }),
      refresh: () => dispatch({ type: 'REFRESH' }),
    }),
    [state],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside <AppProvider>');
  return ctx;
}
